import React, { useState, useEffect } from "react";
import {
    Avatar,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Select,
    MenuItem,
    TextField,
    Typography,
    FormControl,
    InputLabel,
    FormHelperText,
    Container,
    CircularProgress
} from "@mui/material";
import { useCustomAuth } from "../contexts/AuthContext";
import Sidebar from "../components/Sidebar";
import { useNavigate } from "react-router-dom";
import { UserProfile } from "../components/UserProfile";
import { useAuth } from "react-oidc-context";
import { AWS_LAMBDA_ENDPOINT } from "../utils/constants";
import { authFetch } from "../utils/authFetch";

const Users = ({ handleLogout }) => {
    const auth = useAuth();
    const userName = auth.user?.profile?.name || "User";
    const { userData, allUsers, allUserRoles } = useCustomAuth();
    const roles = userData?.roles || [];
    const isAdmin = roles.includes("ADMIN");
    const isRoot = roles.includes("ROOT");
    const navigate = useNavigate();

    // Local state for users (editable notes and roles)
    const [users, setUsers] = useState([]);
    const [loadingRow, setLoadingRow] = useState(null);

    useEffect(() => {
        if (!allUserRoles || !allUsers) {
            return;
        }
        // build a quick lookup by userId
        const lookup = allUserRoles.reduce((acc, user) => {
            acc[user.userId] = user;
            return acc;
        }, {});
        
        // now do the left‐join
        const merged = allUsers.map(user => {
            const match = lookup[user.email];
            return match
            ? { ...user, ...match }
            : user;
        });

        // console.log("Merged users: ", merged);

        const sorted = [...merged].sort((a, b) =>
            a.name.localeCompare(b.name)
        );
        setUsers(sorted);
    }, [allUsers, allUserRoles]);

    const handleRoleChange = async (id, newRole) => {
        setLoadingRow(id);
        try {
            const resDelete = await authFetch(
                `${AWS_LAMBDA_ENDPOINT}/manageUserRoles?user_id=${encodeURIComponent(id)}`,
                {
                    method: "DELETE",
                    body: JSON.stringify({ roles: ["USER", "ADMIN", "ROOT"] }),
                }
            );
        
            if (!resDelete.ok) {
                const err = await resPost.text();
                throw new Error(`(${resDelete.status}) ${err}`);
            }
            // console.log(`✅ [DELETE] '${id}' → Roles: ["USER", "ADMIN", "ROOT"]`);

            const resPost = await authFetch(
                `${AWS_LAMBDA_ENDPOINT}/manageUserRoles?user_id=${encodeURIComponent(id)}`,
                {
                    method: "POST",
                    body: JSON.stringify({ roles: [newRole] }),
                }
            );
        
            if (!resPost.ok) {
                const err = await resPost.text();
                throw new Error(`(${resPost.status}) ${err}`);
            }
        
            const result = await resPost.json();
            // console.log(`✅ [POST] '${id}' → Roles: [${newRole}]`);

            // Update frontend
            setUsers(prev =>
                prev.map(u => {
                    if (u.email === id) {
                        let isRoot = false;
                        let isAdmin = false;
                        if (newRole === "ROOT") {
                            isRoot = true;
                        } else if (newRole === "ADMIN") {
                            isAdmin = true;
                        }
                        return {...u, isRoot, isAdmin};
                    }
                    return u;
                })
            );
            return result;
        } finally {
            setLoadingRow(null);
        }
    };

    const handleNotesChange = (id, newNotes) => {
        setUsers(prev =>
            prev.map(user =>
            user.email === id
                ? { ...user, notes: newNotes }
                : user
            )
        );
    };

    return (
        <Box sx={{ display: 'flex' }}>
            <Sidebar />
            <Box sx={{ marginLeft: '200px', flexGrow: 1 }}>
                {/* Top Navigation Bar */}
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    py: 2, 
                    px: 3, 
                    borderBottom: '1px solid #eaeaea'
                }}>
                    {/* Logo (NeuroMuse AI) - Left */}
                    <Box sx={{ flexBasis: '250px' }}>
                        <Box sx={{ position: "relative", display: "inline-block" }}>
                        <Typography 
                            variant="h5" 
                            className="gradient-text font-header"
                            sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                            onClick={() => {
                            navigate('/');
                            }}
                        >
                            NeuroMuse AI
                        </Typography>
                        {/* Musical Notes */}
                        <span className="musical-note note-1">♪</span>
                        <span className="musical-note note-2">♫</span>
                        <span className="musical-note note-3">♪</span>
                        </Box>
                    </Box>
                    
                    {/* Empty Center Section */}
                    <Box sx={{ 
                        flexGrow: 1, 
                        display: 'flex', 
                        justifyContent: 'center'
                    }}>
                        {/* Removed the center NeuroMuse title and musical notes */}
                    </Box>
                    
                    {/* User Profile - Right */}
                    <UserProfile handleLogout={handleLogout} userName={userName} />
                </Box>
                <Container maxWidth="lg" sx={{ py: 4 }}>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Preferred Username</TableCell>
                                <TableCell>Email Address</TableCell>
                                <TableCell>Role</TableCell>
                                <TableCell>Notes</TableCell>
                            </TableRow>
                            </TableHead>
                            <TableBody>
                            {users.map(user => {
                                const isRowRoot = user.isRoot;
                                // Determine if role select should be disabled
                                let disableRoleSelect = true;
                                if (isRoot) {
                                // Root Admin can change roles between USER/ADMIN but not change ROOT
                                disableRoleSelect = isRowRoot;
                                } else if (isAdmin) {
                                // Admin cannot edit roles at all
                                disableRoleSelect = true;
                                }

                                // Notes editable if admin or root
                                const disableNotes = !(isAdmin || isRoot);

                                return (
                                <TableRow key={user.email} hover>
                                    <TableCell>
                                    <Typography>{user.name}</Typography>
                                    </TableCell>
                                    <TableCell>
                                    <Typography>{user.preferred_username}</Typography>
                                    </TableCell>
                                    <TableCell>
                                    <Typography>{user.email}</Typography>
                                    </TableCell>
                                    <TableCell sx={{position: "relative"}}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel id={`role-label-${user.email}`}>Role</InputLabel>
                                        <Select
                                            labelId={`role-label-${user.email}`}
                                            value={user.isRoot ? "ROOT" : (user.isAdmin ? "ADMIN" : "USER")}
                                            label="Role"
                                            disabled={disableRoleSelect || loadingRow === user.email}
                                            onChange={e => handleRoleChange(user.email, e.target.value)}
                                        >
                                        <MenuItem value="USER">User</MenuItem>
                                        <MenuItem value="ADMIN">Admin</MenuItem>
                                        <MenuItem value="ROOT" disabled>
                                            Root Admin
                                        </MenuItem>
                                        </Select>
                                        {isAdmin && !isRoot && (
                                        <FormHelperText>
                                            Only root admins can change roles.
                                        </FormHelperText>
                                        )}
                                    </FormControl>
                                    {loadingRow === user.email && (
                                        <CircularProgress 
                                        size={20}
                                        sx={{
                                            position: "absolute",
                                            top: "35%",
                                            right: "25%",
                                        }}
                                        />
                                    )}
                                    </TableCell>
                                    <TableCell>
                                    <TextField
                                        value={user.notes}
                                        onChange={e => handleNotesChange(user.email, e.target.value)}
                                        placeholder="Add notes..."
                                        multiline
                                        fullWidth
                                        size="small"
                                        disabled={disableNotes}
                                    />
                                    </TableCell>
                                </TableRow>
                                );
                            })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Container>
            </Box>
        </Box>
    );
};

export default Users;
